import { UAParser } from 'ua-parser-js';
import browserslist from 'browserslist';

/**
 * Attempts to normalize UAParser browser names to match Browserslist keys.
 * For example Mobile Safare is ios_saf in Browserslist, so normalization would not work.
 * Includes minimal essential remaps to ensure accurate matching.
 */
function normalizeBrowserName(name: string): string {
  const lower = name.trim().toLowerCase();

  switch (lower) {
    case 'mobile safari':
      return 'ios_saf';
    case 'samsung internet':
      return 'samsung';
    case 'ie mobile':
      return 'ie_mob';
    case 'opera mobile':
      return 'op_mob';
    case 'android browser':
      return 'and_saf';
    case 'uc browser':
      return 'and_uc';
    case 'qq browser':
      return 'and_qq';
    case 'firefox mobile':
    case 'firefox for android':
      return 'and_ff';
    case 'chrome mobile':
    case 'chrome for android':
      return 'and_chr';
    default:
      // fallback: lowercased, no spaces
      return lower.replace(/\s+/g, '_');
  }
}

/**
 * Maps Browserslist output to a dictionary of minimum versions per browser.
 */
function mapSupportedVersions(browsers: string[]): Record<string, number> {
  const versions: Record<string, number> = {};

  for (const entry of browsers) {
    const [key, versionRange] = entry.split(' ');

    const versionStr = versionRange?.split('-')[0];
    const version = versionStr ? parseInt(versionStr, 10) : NaN;

    if (key && !Number.isNaN(version)) {
      const current = versions[key];

      versions[key] = current === undefined || version < current ? version : current;
    }
  }

  return versions;
}

/**
 * Determines whether a browser (parsed from user-agent) is too old based on a Browserslist query.
 *
 * @param userAgent - The browser's user-agent string.
 * @returns true if the browser is below the supported version or unrecognized; false otherwise.
 */
export function isBrowserTooOld(userAgent: string): boolean {
  // it will automatically read from .browserslistrc
  const supported = browserslist();
  const minVersions = mapSupportedVersions(supported);

  const parser = new UAParser(userAgent);
  const { name, major } = parser.getBrowser();

  if (!name || !major) {
    return true; // Unknown browser → treat as too old
  }

  const normalizedName = normalizeBrowserName(name);
  const browserVersion = parseInt(major, 10);

  if (!minVersions.hasOwnProperty(normalizedName)) {
    return true; // Not listed in supported browsers → treat as too old
  }

  const minVersion = minVersions[normalizedName];

  return browserVersion < minVersion;
}
